// Performance readout for one plan. The figures are read-only.
// Suggested next steps render when the caller supplies them. Accepting one
// posts a new plan id. It does not write to Meta.

import type { ReactNode } from 'react';
import {
  Badge, Box, Image, SimpleGrid, Table, Tbody, Td, Text, Th, Thead, Tr, VStack
} from '@chakra-ui/react';
import { formatMoney } from '../../marketingPlans/client';
import type { AcceptSuggestionResult, PlanSuggestionView } from '../../marketingPlans/drafts';
import { SuggestionsList } from './SuggestionsList';
import type {
  ColorwayResult, CreativeGroup, CreativeResult, ForecastTotals, PlanPerformance, SpendSplit, TestMetrics, TestReadout
} from '../../marketingPlans/performance';

const METRICS: { key: keyof TestMetrics; label: string; format: 'money' | 'roas' | 'count' }[] = [
  { key: 'spend', label: 'Spend', format: 'money' },
  { key: 'roas', label: 'ROAS', format: 'roas' },
  { key: 'cpa', label: 'CPA', format: 'money' },
  { key: 'revenue', label: 'Revenue', format: 'money' },
  { key: 'purchases', label: 'Purchases', format: 'count' }
];

export function PlanPerformanceView({
  model, suggestionsEnabled = false, suggestions, suggestionsLoading = false, onAcceptSuggestion
}: {
  model: PlanPerformance;
  suggestionsEnabled?: boolean;
  suggestions?: PlanSuggestionView[] | null;
  suggestionsLoading?: boolean;
  onAcceptSuggestion?: (suggestion: PlanSuggestionView) => Promise<AcceptSuggestionResult>;
}) {
  const actualsEmpty = !splitPresent(model.spendToDate)
    && model.series.every((point) => point.actual == null);
  const showSparseSections = !model.unavailableReason;
  return (
    <VStack align="stretch" spacing={6} pb={6} data-testid="plan-performance">
      <HeaderMeta model={model} />
      {model.unavailableReason && (
        <Box role="status" borderWidth="1px" borderColor="orange.300" bg="orange.50" borderRadius="md" px={3} py={3} data-testid="performance-unavailable">
          <Text fontSize="sm" color="brand.ink">{model.unavailableReason}</Text>
          {actualsEmpty && (
            <Text fontSize="sm" color="brand.ink" mt={2} data-testid="actuals-explanation">
              Pacing tiles and the forecast stay on this page. Actuals stay empty until this plan is pushed and has live results.
            </Text>
          )}
        </Box>
      )}
      <ForecastTiles model={model} />
      <Pacing model={model} emptyActuals={Boolean(model.unavailableReason) && !splitPresent(model.spendToDate)} />
      {(showSparseSections || model.tests.length > 0) && <Tests model={model} />}
      <RevenueChart model={model} />
      {(showSparseSections || model.creativeGroups.length > 0) && <Creatives groups={model.creativeGroups} currency={model.currency} />}
      {(showSparseSections || model.colorways.length > 0) && <Colorways rows={model.colorways} currency={model.currency} />}
      {suggestionsEnabled && (
        <Section title="Suggested next steps">
          {suggestionsLoading ? (
            <Text fontSize="sm" color="brand.muted" data-testid="suggestions-loading">Loading suggestions…</Text>
          ) : suggestions === undefined ? (
            <Text fontSize="sm" color="brand.muted" data-testid="suggestions-placeholder">
              Suggestions will show up here once the planning model is connected. Accepting one will open a draft plan revision. This page does not change Meta.
            </Text>
          ) : (
            <SuggestionsList suggestions={suggestions} onAccept={onAcceptSuggestion} />
          )}
        </Section>
      )}
    </VStack>
  );
}

function HeaderMeta({ model }: { model: PlanPerformance }) {
  const bits = [
    model.revision ? `Revision ${model.revision}` : null,
    model.status,
    model.flightLabel ? `Flight ${model.flightLabel}` : null,
    model.refreshedLabel ? `Refreshed ${model.refreshedLabel}` : null
  ].filter((bit): bit is string => Boolean(bit));
  if (bits.length === 0 && !model.paceLabel) return null;
  return (
    <VStack align="stretch" spacing={2} data-testid="performance-header">
      {bits.length > 0 && (
        <Text fontSize="sm" color="brand.muted">{bits.join(' · ')}</Text>
      )}
      {model.paceLabel && <PaceBadge label={model.paceLabel} />}
    </VStack>
  );
}

function PaceBadge({ label }: { label: string }) {
  const color = label === 'Behind' ? 'orange' : label === 'Ahead' ? 'green' : 'gray';
  return <Badge alignSelf="flex-start" colorScheme={color} data-testid="pace-status">{label}</Badge>;
}

function ForecastTiles({ model }: { model: PlanPerformance }) {
  if (!model.forecast) return null;
  const rows = forecastRows(model.forecast, model.currency);
  if (rows.length === 0) return null;
  return (
    <Section title="Forecast">
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2} data-testid="forecast-tiles">
        {rows.map((row) => (
          <Box key={row.label} borderWidth="1px" borderColor="brand.border" borderRadius="md" px={3} py={2} data-testid="forecast-tile">
            <Text fontSize="xs" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em">{row.label}</Text>
            <Text fontSize="sm" color="brand.ink">{row.value}</Text>
          </Box>
        ))}
      </SimpleGrid>
    </Section>
  );
}

function forecastRows(forecast: ForecastTotals, currency: string | null): { label: string; value: string }[] {
  const rows = [
    { label: 'Forecast spend', value: money(forecast.spend, currency) },
    { label: 'Forecast revenue', value: money(forecast.revenue, currency) },
    { label: 'Incremental revenue', value: money(forecast.incremental, currency) },
    { label: 'Forecast purchases', value: countOrMissing(forecast.purchases) },
    { label: 'Forecast ROAS', value: roas(forecast.roas) },
    { label: 'Forecast CPA', value: money(forecast.cpa, currency) }
  ];
  return rows.filter((row) => row.value !== 'Not available');
}

function Pacing({ model, emptyActuals }: { model: PlanPerformance; emptyActuals: boolean }) {
  return (
    <Section title="Pacing">
      <VStack align="stretch" spacing={3} data-testid="pacing-section">
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
          <SplitTile
            label="Spend to date"
            split={model.spendToDate}
            currency={model.currency}
            testId="spend-to-date"
            emptyLabel={emptyActuals ? 'No actuals yet' : 'Not in this response'}
          />
          <SplitTile label="Weekly budget" split={model.weekly} currency={model.currency} testId="weekly-budget" emptyLabel="Not in this response" />
          <SplitTile label="Flight budget" split={model.flight} currency={model.currency} testId="flight-budget" emptyLabel="Not in this response" />
        </SimpleGrid>
        {model.lines.length === 0 ? <Muted>No pacing lines recorded.</Muted> : (
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Line</Th>
                  <Th>Spend class</Th>
                  <Th isNumeric>Weekly new</Th>
                  <Th isNumeric>Weekly existing</Th>
                  <Th isNumeric>Flight new</Th>
                  <Th isNumeric>Flight existing</Th>
                  <Th isNumeric>Spent new</Th>
                  <Th isNumeric>Spent existing</Th>
                </Tr>
              </Thead>
              <Tbody>
                {model.lines.map((line, index) => (
                  <Tr key={`${line.name}-${index}`} data-testid="pacing-line">
                    <Td>{line.name}</Td>
                    <Td>{classLabel(line.spendClass)}</Td>
                    <Td isNumeric>{splitAmount(line.weekly, 'new', model.currency)}</Td>
                    <Td isNumeric>{splitAmount(line.weekly, 'existing', model.currency)}</Td>
                    <Td isNumeric>{splitAmount(line.flight, 'new', model.currency)}</Td>
                    <Td isNumeric>{splitAmount(line.flight, 'existing', model.currency)}</Td>
                    <Td isNumeric>{splitAmount(line.spent, 'new', model.currency)}</Td>
                    <Td isNumeric>{splitAmount(line.spent, 'existing', model.currency)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </VStack>
    </Section>
  );
}

function SplitTile({
  label, split, currency, testId, emptyLabel = 'Not in this response'
}: {
  label: string;
  split: SpendSplit;
  currency: string | null;
  testId: string;
  emptyLabel?: string;
}) {
  const empty = !splitPresent(split);
  const newSpend = split.newSpend;
  const existingSpend = split.existingSpend;
  const total = split.unclassified != null ? splitTotal(split) : null;
  return (
    <Box borderWidth="1px" borderColor="brand.border" borderRadius="md" px={3} py={2} data-testid={testId}>
      <Text fontSize="xs" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em">{label}</Text>
      {empty ? <Text fontSize="sm" color="brand.ink">{emptyLabel}</Text> : (
        <>
          {newSpend != null && <Text fontSize="sm" color="brand.ink">New {money(newSpend, currency)}</Text>}
          {existingSpend != null && <Text fontSize="sm" color="brand.ink">Existing {money(existingSpend, currency)}</Text>}
          {total != null && <Text fontSize="sm" color="brand.ink">Total {money(total, currency)}</Text>}
        </>
      )}
    </Box>
  );
}

function Tests({ model }: { model: PlanPerformance }) {
  return (
    <Section title="Tests">
      {model.tests.length === 0 ? <Muted>No test readouts yet.</Muted> : (
        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={3}>
          {model.tests.map((test, index) => <TestCard key={`${test.name}-${index}`} test={test} currency={model.currency} />)}
        </SimpleGrid>
      )}
    </Section>
  );
}

function TestCard({ test, currency }: { test: TestReadout; currency: string | null }) {
  return (
    <Box borderWidth="1px" borderColor="brand.border" borderRadius="md" p={3} data-testid="test-readout">
      <VStack align="stretch" spacing={2}>
        <Text fontWeight="700" color="brand.ink">{test.name}</Text>
        {test.callLabel && <CallBadge label={test.callLabel} />}
        {(test.confidenceLabel || test.daysLabel) && (
          <Text fontSize="xs" color="brand.muted">
            {[test.confidenceLabel ? `Confidence ${test.confidenceLabel}` : null, test.daysLabel].filter(Boolean).join(' · ')}
          </Text>
        )}
        <Box overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Metric</Th>
                <Th isNumeric>Ours actual</Th>
                <Th isNumeric>Ours forecast</Th>
                <Th isNumeric>Control actual</Th>
                <Th isNumeric>Control forecast</Th>
              </Tr>
            </Thead>
            <Tbody>
              {METRICS.map((metric) => (
                <Tr key={metric.key}>
                  <Td>{metric.label}</Td>
                  <Td isNumeric>{metricText(test.ours[metric.key].actual, metric.format, currency)}</Td>
                  <Td isNumeric>{metricText(test.ours[metric.key].forecast, metric.format, currency)}</Td>
                  <Td isNumeric>{metricText(test.control[metric.key].actual, metric.format, currency)}</Td>
                  <Td isNumeric>{metricText(test.control[metric.key].forecast, metric.format, currency)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </VStack>
    </Box>
  );
}

function CallBadge({ label }: { label: string }) {
  const color = label === 'Ours ahead' ? 'green' : label === 'Control ahead' ? 'orange' : 'gray';
  return <Badge alignSelf="flex-start" colorScheme={color} data-testid="test-call">{label}</Badge>;
}

function RevenueChart({ model }: { model: PlanPerformance }) {
  const actualsEmpty = model.series.every((point) => point.actual == null);
  return (
    <Section title="Forecast vs actual revenue">
      {model.series.length === 0 ? (
        <Muted>
          {model.unavailableReason
            ? 'No daily forecast or actual revenue in this response. Actuals appear after this plan is pushed and has live results.'
            : 'No daily revenue yet.'}
        </Muted>
      ) : (
        <VStack align="stretch" spacing={3} data-testid="revenue-chart">
          <RevenueLines points={model.series} />
          <HStackLegend />
          {actualsEmpty && (
            <Text fontSize="sm" color="brand.muted" data-testid="actuals-empty">No actual revenue yet.</Text>
          )}
          <Box overflowX="auto">
            <Table size="sm" variant="simple" data-testid="revenue-chart-table">
              <Thead>
                <Tr>
                  <Th>Day</Th>
                  <Th isNumeric>Cumulative forecast</Th>
                  <Th isNumeric>Cumulative actual</Th>
                </Tr>
              </Thead>
              <Tbody>
                {model.series.map((point) => (
                  <Tr key={point.dateLabel}>
                    <Td>{point.dateLabel}</Td>
                    <Td isNumeric>{money(point.forecast, model.currency)}</Td>
                    <Td isNumeric>{money(point.actual, model.currency)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </VStack>
      )}
    </Section>
  );
}

function RevenueLines({ points }: { points: PlanPerformance['series'] }) {
  const width = 640;
  const height = 180;
  const pad = { l: 12, r: 12, t: 12, b: 16 };
  const values = points.flatMap((point) => [point.forecast, point.actual].filter((value): value is number => value != null));
  const min = Math.min(0, ...values, 0);
  const max = Math.max(0, ...values, 0);
  const span = max - min || 1;
  const x = (index: number) => {
    if (points.length <= 1) return (width - pad.l - pad.r) / 2 + pad.l;
    return pad.l + (index / (points.length - 1)) * (width - pad.l - pad.r);
  };
  const y = (value: number) => pad.t + (1 - (value - min) / span) * (height - pad.t - pad.b);
  return (
    <Box>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img" aria-label="Cumulative forecast versus actual revenue by day">
        <title>Cumulative forecast versus actual revenue by day</title>
        <path d={linePath(points.map((point) => point.forecast), x, y)} fill="none" stroke="#8B8FA3" strokeWidth="2" />
        <path d={linePath(points.map((point) => point.actual), x, y)} fill="none" stroke="#7A35E8" strokeWidth="2" />
        {points.map((point, index) => (
          <g key={point.dateLabel}>
            {point.forecast != null && <circle cx={x(index)} cy={y(point.forecast)} r="2.5" fill="#8B8FA3" />}
            {point.actual != null && <circle cx={x(index)} cy={y(point.actual)} r="2.5" fill="#7A35E8" />}
          </g>
        ))}
      </svg>
    </Box>
  );
}

function HStackLegend() {
  return (
    <Box>
      <Text as="span" fontSize="xs" color="brand.muted" mr={4}>Forecast</Text>
      <Text as="span" fontSize="xs" color="brand.ink">Actual</Text>
    </Box>
  );
}

function linePath(
  values: (number | null)[],
  x: (index: number) => number,
  y: (value: number) => number
): string {
  let path = '';
  let drawing = false;
  values.forEach((value, index) => {
    if (value == null) {
      drawing = false;
      return;
    }
    path += `${drawing ? 'L' : 'M'}${x(index).toFixed(1)},${y(value).toFixed(1)} `;
    drawing = true;
  });
  return path.trim();
}

function Creatives({ groups, currency }: { groups: CreativeGroup[]; currency: string | null }) {
  return (
    <Section title="Creatives">
      {groups.length === 0 ? <Muted>No creative results yet.</Muted> : (
        <VStack align="stretch" spacing={4}>
          {groups.map((group) => (
            <Box key={group.key} data-testid="creative-group">
              <Text fontSize="sm" fontWeight="700" color="brand.ink" mb={2}>{group.label}</Text>
              <VStack align="stretch" spacing={2}>
                {group.rows.map((row, index) => <CreativeRow key={`${row.name}-${index}`} row={row} currency={currency} />)}
              </VStack>
            </Box>
          ))}
        </VStack>
      )}
    </Section>
  );
}

function CreativeRow({ row, currency }: { row: CreativeResult; currency: string | null }) {
  return (
    <Box borderWidth="1px" borderColor="brand.border" borderRadius="md" p={3} data-testid="creative-row">
      <Box display="flex" gap={3} alignItems="flex-start">
        {row.thumbnailUrl ? (
          <Image src={row.thumbnailUrl} alt={row.name} referrerPolicy="no-referrer" boxSize="48px" objectFit="cover" borderRadius="md" data-testid="creative-thumb" />
        ) : (
          <Box boxSize="48px" borderRadius="md" bg="blackAlpha.50" display="flex" alignItems="center" justifyContent="center" data-testid="creative-thumb">
            <Text fontSize="10px" color="brand.muted" textAlign="center" px={1}>No thumbnail</Text>
          </Box>
        )}
        <Box minW={0}>
          <Text fontSize="sm" fontWeight="700" color="brand.ink">{row.name}</Text>
          {row.colorway && <Text fontSize="xs" color="brand.muted">Colorway {row.colorway}</Text>}
          <Text fontSize="xs" color="brand.ink" mt={1}>
            Spend {money(row.spend, currency)} · CTR {ctr(row.ctr)} · CPA {money(row.cpa, currency)} · ROAS {roas(row.roas)}
          </Text>
          {row.fatigue && <Badge mt={2} colorScheme="orange" data-testid="fatigue-flag">{row.fatigue}</Badge>}
        </Box>
      </Box>
    </Box>
  );
}

function Colorways({ rows, currency }: { rows: ColorwayResult[]; currency: string | null }) {
  return (
    <Section title="Colorways and stock">
      {rows.length === 0 ? <Muted>No colorway results yet.</Muted> : (
        <Box overflowX="auto">
          <Table size="sm" variant="simple" data-testid="colorway-table">
            <Thead>
              <Tr>
                <Th>Colorway</Th>
                <Th isNumeric>In stock</Th>
                <Th>Stock</Th>
                <Th isNumeric>Spend</Th>
                <Th isNumeric>Revenue</Th>
                <Th isNumeric>ROAS</Th>
                <Th isNumeric>Purchases</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row, index) => (
                <Tr key={`${row.name}-${index}`} data-testid="colorway-row">
                  <Td>{row.name}</Td>
                  <Td isNumeric>{row.inStock == null ? 'Not available' : count(row.inStock)}</Td>
                  <Td>
                    {row.belowRule
                      ? <Badge colorScheme="orange" data-testid="stock-flag">Below the {formatRule(row.sizeRule)}-size in-stock rule</Badge>
                      : row.inStock == null ? 'Not available' : 'Meets the in-stock rule'}
                  </Td>
                  <Td isNumeric>{money(row.spend, currency)}</Td>
                  <Td isNumeric>{money(row.revenue, currency)}</Td>
                  <Td isNumeric>{roas(row.roas)}</Td>
                  <Td isNumeric>{countOrMissing(row.purchases)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Section>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box>
      <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={2}>
        {title}
      </Text>
      {children}
    </Box>
  );
}

function Muted({ children }: { children: string }) {
  return <Text fontSize="sm" color="brand.muted">{children}</Text>;
}

function classLabel(value: 'new' | 'existing' | null): string {
  if (value === 'new') return 'New';
  if (value === 'existing') return 'Existing';
  return 'Not available';
}

function money(value: number | null, currency: string | null): string {
  if (value == null) return 'Not available';
  return formatMoney(value, currency);
}

function roas(value: number | null): string {
  if (value == null) return 'Not available';
  return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 }).format(value)}×`;
}

function count(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
}

function countOrMissing(value: number | null): string {
  if (value == null) return 'Not available';
  return count(value);
}

function splitAmount(split: SpendSplit, side: 'new' | 'existing', currency: string | null): string {
  if (split.unclassified != null && split.newSpend == null && split.existingSpend == null) return '—';
  return money(side === 'new' ? split.newSpend : split.existingSpend, currency);
}

function splitPresent(split: SpendSplit): boolean {
  return split.newSpend != null || split.existingSpend != null || split.unclassified != null;
}

function splitTotal(split: SpendSplit): number | null {
  const parts = [split.newSpend, split.existingSpend, split.unclassified].filter((part): part is number => part != null);
  if (parts.length === 0) return null;
  return parts.reduce((sum, part) => sum + part, 0);
}

function ctr(value: number | null): string {
  if (value == null) return 'Not available';
  // Meta insights, and the performance payload that passes them through,
  // already express CTR as a percent. 1.5 is 1.50%, not 150%.
  return `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)}%`;
}

function formatRule(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
}

function metricText(value: number | null, format: 'money' | 'roas' | 'count', currency: string | null): string {
  if (format === 'money') return money(value, currency);
  if (format === 'roas') return roas(value);
  return countOrMissing(value);
}
